import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Database, Trash2, Download, RefreshCw, AlertTriangle, CheckCircle, Package, Users, Phone, FileText, Calendar, GitBranch } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DemoDataManager() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [showWipeConfirm, setShowWipeConfirm] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/settings/demo-data/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch status:', err);
      toast.error('Failed to load demo data status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const seedDemoData = async () => {
    setSeeding(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API}/settings/demo-data/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Demo data seeded successfully');
      console.log('Seeded:', response.data.created);
      fetchStatus();
    } catch (err) {
      console.error('Failed to seed demo data:', err);
      toast.error('Failed to seed demo data');
    } finally {
      setSeeding(false);
    }
  };

  const wipeDemoData = async () => {
    setWiping(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API}/settings/demo-data/wipe`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Demo data wiped successfully');
      console.log('Deleted:', response.data.deleted);
      setShowWipeConfirm(false);
      fetchStatus();
    } catch (err) {
      console.error('Failed to wipe demo data:', err);
      toast.error('Failed to wipe demo data');
    } finally {
      setWiping(false);
    }
  };

  const categoryConfig = {
    queues: { icon: Phone, label: 'Queues', color: 'text-blue-500' },
    ai_agents: { icon: Users, label: 'AI Agents', color: 'text-purple-500' },
    interactions: { icon: Phone, label: 'Interactions', color: 'text-green-500' },
    scripts: { icon: FileText, label: 'Scripts', color: 'text-orange-500' },
    callbacks: { icon: Calendar, label: 'Callbacks', color: 'text-cyan-500' },
    workflows: { icon: GitBranch, label: 'Workflows', color: 'text-pink-500' },
    contacts: { icon: Users, label: 'Contacts', color: 'text-indigo-500' },
    jobs: { icon: Package, label: 'Jobs', color: 'text-yellow-500' }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold">Demo Data Manager</h2>
          <p className="text-muted-foreground">Manage demo data for the Call Center module</p>
        </div>
        <Button variant="outline" onClick={fetchStatus}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Database className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>Demo Data Status</CardTitle>
                <CardDescription>Current state of demo data in the system</CardDescription>
              </div>
            </div>
            {status?.has_demo_data ? (
              <Badge variant="default" className="bg-green-100 text-green-800">
                <CheckCircle className="h-3 w-3 mr-1" />
                Active
              </Badge>
            ) : (
              <Badge variant="secondary">
                No demo data
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <>
              <div className="mb-4 p-4 bg-muted/50 rounded-lg">
                <div className="text-3xl font-bold mb-1">{status?.total_records || 0}</div>
                <div className="text-sm text-muted-foreground">Total demo records</div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {status?.breakdown && Object.entries(status.breakdown).map(([key, count]) => {
                  const config = categoryConfig[key] || { icon: Package, label: key, color: 'text-gray-500' };
                  const Icon = config.icon;
                  return (
                    <div key={key} className="p-3 border rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={`h-4 w-4 ${config.color}`} />
                        <span className="text-sm text-muted-foreground">{config.label}</span>
                      </div>
                      <div className="text-xl font-semibold">{count}</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Actions Card */}
      <Card>
        <CardHeader>
          <CardTitle>Actions</CardTitle>
          <CardDescription>Seed or wipe demo data</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 p-4 border rounded-lg">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <Download className="h-4 w-4 text-green-500" />
                Seed Demo Data
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Creates realistic demo data including queues, AI agents, sample interactions, 
                scripts, workflows, and test contacts/jobs. Safe to run multiple times.
              </p>
              <Button onClick={seedDemoData} disabled={seeding} className="w-full">
                {seeding ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Seeding...</>
                ) : (
                  <><Download className="h-4 w-4 mr-2" /> Seed Demo Data</>
                )}
              </Button>
            </div>

            <div className="flex-1 p-4 border rounded-lg border-red-200 bg-red-50/50">
              <h3 className="font-semibold mb-2 flex items-center gap-2 text-red-700">
                <Trash2 className="h-4 w-4" />
                Wipe Demo Data
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Permanently removes all demo data from the system. Only affects records 
                marked as demo data - production data is safe.
              </p>
              <Button
                variant="destructive"
                onClick={() => setShowWipeConfirm(true)}
                disabled={!status?.has_demo_data || wiping}
                className="w-full"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Wipe Demo Data
              </Button>
            </div>
          </div>

          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-800">Important Notes</h4>
                <ul className="text-sm text-amber-700 mt-1 space-y-1">
                  <li>• Demo data is marked with a special flag and won't affect production data</li>
                  <li>• Seeding is idempotent - running it multiple times updates existing records</li>
                  <li>• Wiping only removes records marked as demo data</li>
                  <li>• Use this to set up training environments or reset after demonstrations</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Wipe Confirmation Dialog */}
      <Dialog open={showWipeConfirm} onOpenChange={setShowWipeConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Demo Data Wipe
            </DialogTitle>
            <DialogDescription>
              This will permanently delete all demo data ({status?.total_records || 0} records).
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm">The following will be deleted:</p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {status?.breakdown && Object.entries(status.breakdown).map(([key, count]) => {
                const config = categoryConfig[key];
                return count > 0 ? (
                  <div key={key} className="text-sm flex justify-between p-2 bg-red-50 rounded">
                    <span>{config?.label || key}</span>
                    <span className="font-medium text-red-600">{count}</span>
                  </div>
                ) : null;
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWipeConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={wipeDemoData} disabled={wiping}>
              {wiping ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Wiping...</>
              ) : (
                <><Trash2 className="h-4 w-4 mr-2" /> Yes, Wipe All Demo Data</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
